/**
 * Parent-level summary of the line items under a multi-item (umbrella) claim,
 * shown on the parent claim's Overview tab. A parent claim carries no product /
 * customer of its own, so its Overview would otherwise be near-empty - this
 * card surfaces "what is in this claim" at a glance: an item count, a
 * state breakdown, and a per-item row (product, state, assignee) that links
 * through to the child claim. The full list lives on the Items tab.
 *
 * Reuses the shared DetailCard + StatusBadge - nothing here is tenant-specific;
 * every state label/colour comes from the API-supplied claim_state /
 * claim_state_color on each child row.
 */
import { useMemo } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Layers, ChevronRight } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { DetailCard } from "@/admin/components/shared/detail-card";
import { StatusBadge } from "@/admin/components/shared/status-badge";
import { useClaimChildren } from "@/admin/hooks/use-claims";
import { formatClaimId } from "@/admin/lib/formatting";

/** How many item rows to show before deferring to the Items tab. */
const PREVIEW_ITEMS = 6;

export function ClaimItemsSummaryCard({
  parentUuid,
  onViewAll,
}: {
  parentUuid: string;
  /** Switch to the Items tab (wired by the detail page). */
  onViewAll?: () => void;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    data: children = [],
    isPending,
    isFetching,
  } = useClaimChildren(parentUuid);

  // Count per distinct state, most-common first. Label + colour are whatever
  // the workflow assigned (backend-supplied), never hardcoded.
  const stateBreakdown = useMemo(() => {
    const map = new Map<
      string,
      { label: string; color?: string; count: number }
    >();
    for (const c of children) {
      const key = c.claim_state || "unknown";
      const entry = map.get(key);
      if (entry) entry.count += 1;
      else
        map.set(key, {
          label: c.claim_state || t("claims.stateUnknown", "Unknown"),
          color: c.claim_state_color,
          count: 1,
        });
    }
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [children, t]);

  const preview = children.slice(0, PREVIEW_ITEMS);
  const remaining = children.length - preview.length;

  return (
    <DetailCard
      title={t("claims.claimItemsCardTitle", "Claim Items")}
      icon={<Layers className="h-3.5 w-3.5" />}
      collapsible={false}
      trailing={
        children.length > 0 ? (
          <span className="shrink-0 rounded-full bg-[var(--bg-light)] px-2 py-0.5 text-[11px] text-[var(--ink-3)]">
            {children.length}
          </span>
        ) : undefined
      }
    >
      {isPending ? (
        <div className="px-4 py-6 text-center text-[12px] text-[var(--ink-3)]">
          {t("common.loading", "Loading...")}
        </div>
      ) : children.length === 0 ? (
        <div className="px-4 py-6 text-center text-[12px] text-[var(--ink-3)]">
          {t("claims.noItemsInClaim", "No items in this claim yet.")}
        </div>
      ) : (
        <>
          {/* State breakdown strip */}
          {stateBreakdown.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-b-[0.5px] border-b-[var(--rule-light)] px-4 py-3">
              {stateBreakdown.map((s) => (
                <span key={s.label} className="flex items-center gap-1.5">
                  <StatusBadge status={s.label} color={s.color} />
                  <span className="text-[12px] text-[var(--ink-3)]">
                    {s.count}
                  </span>
                </span>
              ))}
            </div>
          )}

          {/* Per-item rows */}
          {preview.map((item) => (
            <Button
              key={item.uuid}
              variant="ghost"
              onClick={() => navigate(`/claims/${item.uuid}?tab=overview`)}
              className="flex h-auto w-full items-center justify-between gap-3 rounded-none border-b-[0.5px] border-b-[var(--rule-light)] px-4 py-[9px] text-left font-normal transition-colors hover:bg-[var(--bg-subtle)]"
            >
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-sm text-[var(--ink)]">
                  {item.product_name ||
                    item.claim_number ||
                    formatClaimId(item.uuid)}
                </span>
                <span className="truncate text-[11px] text-[var(--ink-3)]">
                  {item.consumer_account_name ||
                    item.consumer_account_email ||
                    formatClaimId(item.uuid)}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {item.claim_state && (
                  <StatusBadge
                    status={item.claim_state}
                    color={item.claim_state_color}
                  />
                )}
                <span className="hidden max-w-[120px] truncate text-[11px] text-[var(--ink-3)] sm:inline">
                  {item.assigned_to?.name ||
                    t("claims.unassigned", "Unassigned")}
                </span>
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
              </div>
            </Button>
          ))}

          {/* View-all deferral to the Items tab */}
          {(remaining > 0 || onViewAll) && (
            <Button
              variant="ghost"
              onClick={onViewAll}
              disabled={!onViewAll}
              className="flex h-auto w-full items-center justify-center gap-1.5 rounded-none rounded-b-[8px] py-2.5 text-sm font-normal text-[var(--ink-3)] transition-colors hover:text-[var(--ink)] disabled:cursor-default disabled:hover:text-[var(--ink-3)]"
            >
              {remaining > 0
                ? t("claims.viewAllItems", "View all {{count}} items", {
                    count: children.length,
                  })
                : t("claims.viewItemsTab", "View items")}
              <ChevronRight className="h-3 w-3" />
            </Button>
          )}
        </>
      )}
      {/* Background refetch indicator kept subtle; rows already painted. */}
      {!isPending && isFetching && (
        <span className="sr-only">{t("common.loading", "Loading...")}</span>
      )}
    </DetailCard>
  );
}
